import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#restorePreviousPages().then(
      () => this.#observeViewMore(),
      () => this.#observeViewMore()
    );

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    await yieldToMainThread();

    history.replaceState(history.state, '', nextPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async renderNextPage() {
    await this.#renderNextPage();
  }

  async #restorePreviousPages() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    const isInitiallyHidden = grid.hasAttribute('data-nobo-restoring-previous-pages');
    if (!previousPage || !this.#shouldUsePage(previousPage)) {
      if (isInitiallyHidden) {
        grid.style.removeProperty('visibility');
        grid.removeAttribute('data-nobo-restoring-previous-pages');
      }
      return;
    }

    const pageNumbers = Array.from({ length: previousPage.page }, (_, index) => index + 1);
    const currentScrollTop = getScrollTop();
    const persistentItems = grid.querySelectorAll(':scope > [data-nobo-persistent-grid-item]');
    const persistentFirstItem = persistentItems[0];
    const persistentAnchor = persistentItems[persistentItems.length - 1];
    const firstProduct = Array.isArray(this.refs.cards) ? this.refs.cards[0] : undefined;
    const anchorElement = persistentFirstItem && firstProduct ? firstProduct : grid.firstElementChild;
    const persistentItemRect = persistentFirstItem?.getBoundingClientRect();
    const shouldMaintainAnchor =
      currentScrollTop > 0 && (!persistentFirstItem || (persistentItemRect && persistentItemRect.bottom <= 0));
    const oldHeight = anchorElement ? anchorElement.getBoundingClientRect().top + currentScrollTop : 0;
    const previousVisibility = isInitiallyHidden ? '' : grid.style.visibility;
    const previousAriaBusy = grid.getAttribute('aria-busy');

    grid.style.visibility = 'hidden';
    grid.setAttribute('aria-busy', 'true');

    try {
      await Promise.all(pageNumbers.map((pageNumber) => this.#fetchSpecificPage(pageNumber)));

      const previousPageItemElements = pageNumbers.flatMap((pageNumber) => this.#getGridForPage(pageNumber) ?? []);
      if (previousPageItemElements.length === 0) return;

      if (persistentAnchor) {
        persistentAnchor.after(...previousPageItemElements);
      } else {
        grid.prepend(...previousPageItemElements);
      }

      this.#aspectRatioHelper.processNewElements();

      if (anchorElement && shouldMaintainAnchor) {
        const newHeight = anchorElement.getBoundingClientRect().top + getScrollTop();
        const heightDiff = newHeight - oldHeight;
        scrollTo({
          top: currentScrollTop + heightDiff,
          behavior: 'instant',
        });
      } else if (currentScrollTop === 0) {
        scrollTo({
          top: 0,
          behavior: 'instant',
        });
      }
    } finally {
      grid.style.visibility = previousVisibility;
      grid.removeAttribute('data-nobo-restoring-previous-pages');
      if (previousAriaBusy === null) {
        grid.removeAttribute('aria-busy');
      } else {
        grid.setAttribute('aria-busy', previousAriaBusy);
      }
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    const persistentItems = grid.querySelectorAll(':scope > [data-nobo-persistent-grid-item]');
    const persistentFirstItem = persistentItems[0];
    const persistentAnchor = persistentItems[persistentItems.length - 1];

    // Store the current scroll position and the element that should remain visually anchored.
    const currentScrollTop = getScrollTop();
    const firstProduct = Array.isArray(this.refs.cards) ? this.refs.cards[0] : undefined;
    const anchorElement = persistentFirstItem && firstProduct ? firstProduct : grid.firstElementChild;
    const persistentItemRect = persistentFirstItem?.getBoundingClientRect();
    const shouldMaintainAnchor =
      !persistentFirstItem || (currentScrollTop > 0 && persistentItemRect && persistentItemRect.bottom <= 0);
    const oldHeight = anchorElement ? anchorElement.getBoundingClientRect().top + currentScrollTop : 0;

    // Keep images that sit before the first product ahead of restored pages.
    if (persistentAnchor) {
      persistentAnchor.after(...previousPageItemElements);
    } else {
      grid.prepend(...previousPageItemElements);
    }

    this.#aspectRatioHelper.processNewElements();

    // Calculate and adjust scroll position to maintain the same view
    if (anchorElement && shouldMaintainAnchor) {
      const newHeight = anchorElement.getBoundingClientRect().top + getScrollTop();
      const heightDiff = newHeight - oldHeight;
      scrollTo({
        top: currentScrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    await yieldToMainThread();

    requestIdleCallback(async () => {
      await this.#fetchPage('previous');
      this.#renderPreviousPage();
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {Element[] | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    const itemElements = Array.from(gridElement.querySelectorAll(':scope > .product-grid__item'));
    const persistentFirstItem = this.refs.grid?.querySelector(':scope > [data-nobo-persistent-grid-item]');

    if (!persistentFirstItem) return itemElements;

    return itemElements.filter((item) => !item.matches('[data-nobo-persistent-grid-item]'));
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
